import {ActionList} from '@primer/react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {isCopilot} from '@github-ui/assignees/copilot-user'
import {ownerPath} from '@github-ui/paths'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import type {
  AssigneesResponse as AssigneesData,
  Assignee,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/assignees'
import styles from './Assignees.module.css'
import sidebarListStyles from './SidebarActionList.module.css'
import {UserSectionSkeleton} from './UserSectionSkeleton'
import {useAssigneesQuery} from './data/route-queries'
import {useOverviewRouteData} from './data/use-overview-route-data'
import {AssignYourself} from './AssignYourself'

export function Assignees() {
  const {data: assigneesData, isPending, isError} = useAssigneesQuery()
  const {user} = useOverviewRouteData()

  if (isPending && !assigneesData) return <UserSectionSkeleton placeholderRows={1} />

  if (isError && !assigneesData) {
    return <span className={sidebarListStyles.emptyState}>Unable to load assignees</span>
  }

  return <AssigneesComponent assigneesData={assigneesData} canAssign={user?.canAssign ?? false} />
}

function AssigneesComponent({assigneesData, canAssign}: {assigneesData: AssigneesData; canAssign: boolean}) {
  const assignees = [...(assigneesData.assignees ?? [])].sort((a, b) =>
    a.displayLogin.localeCompare(b.displayLogin, undefined, {sensitivity: 'base'}),
  )

  if (assignees.length === 0) {
    return <span className={sidebarListStyles.emptyState}>{canAssign ? <AssignYourself /> : 'No one assigned'}</span>
  }

  return (
    <ActionList variant="full" className={sidebarListStyles.flush}>
      {assignees.map(assignee => (
        <AssigneeRow key={assignee.id} assignee={assignee} />
      ))}
    </ActionList>
  )
}

const COPILOT_DISPLAY_NAME = 'Copilot'

function AssigneeRow({assignee}: {assignee: Assignee}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const copilot = isCopilot(assignee.displayLogin)
  const appBacked = copilot || Boolean(assignee.slug)

  const displayName = copilot ? COPILOT_DISPLAY_NAME : assignee.displayLogin
  const appSlug = assignee.slug || assignee.displayLogin
  const href = appBacked ? `/apps/${appSlug}` : ownerPath({owner: assignee.displayLogin})
  const hovercardAttrs = appBacked
    ? getHovercardAttributesForType('copilot', {botSlug: appSlug})
    : getHovercardAttributesForType('user', {login: assignee.displayLogin})

  return (
    <ActionList.LinkItem href={href} className={styles.assigneeItem} {...hovercardAttrs}>
      <ActionList.LeadingVisual>
        {copilot ? <CopilotAvatar isDecorative /> : <GitHubAvatar src={assignee.avatarUrl} size={20} />}
      </ActionList.LeadingVisual>
      <span className={styles.assigneeName}>{displayName}</span>
    </ActionList.LinkItem>
  )
}
